import React from 'react';
import { TrendingUp, Shield, Target, BarChart3, ArrowRight, PieChart } from 'lucide-react';
import { Link } from 'react-router-dom';

const InvestmentManagementPage = () => {
  const services = [
    {
      icon: BarChart3,
      title: 'Portfolio Management',
      description: 'Our expert team provides comprehensive portfolio management services designed to optimize your investments across various asset classes and market conditions.'
    },
    {
      icon: TrendingUp,
      title: 'Investment Strategy',
      description: 'We develop tailored investment strategies that align with your financial goals, risk tolerance, and time horizon to maximize returns while managing risk.'
    },
    {
      icon: Target,
      title: 'Asset Allocation',
      description: 'Strategic asset allocation across diverse investment sectors including Blockchain-based Assets, Real Estate, Oil and Gas, and Stocks to build a balanced portfolio.'
    },
    {
      icon: Shield,
      title: 'Risk Management',
      description: 'Comprehensive risk assessment and management strategies to protect your investments and ensure long-term financial stability.'
    },
    {
      icon: PieChart,
      title: 'Performance Monitoring',
      description: 'Continuous monitoring and analysis of your investment performance with regular reporting and strategic adjustments as needed.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-navy-900/80 to-navy-800/60"></div>
        <div className="absolute inset-0">
          <img
            src="/images/finance-8836902_1280.jpg"
            alt="Investment Management"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 text-center text-white">
          <h1 className="text-4xl md:text-6xl font-bold font-serif mb-4">Investment Management</h1>
          <p className="text-xl md:text-2xl text-gray-200">
            Home / <span className="text-gold-400">Services</span> / <span className="text-gold-400">Investment Management</span>
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold font-serif text-navy-900 mb-6">
              Comprehensive <span className="text-gold-600">Investment Management Services</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
            <div>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                At COREVEST, we offer comprehensive investment management services designed to help you achieve your financial goals. Our expert team provides tailored strategies and portfolio management to maximize returns while effectively managing risk.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Whether you're looking to grow your wealth, plan for retirement, or diversify your portfolio, our investment management services are customized to meet your unique needs and objectives. We leverage deep market insight, advanced analytics, and a disciplined investment approach to build and manage portfolios that align with your financial aspirations.
              </p>
            </div>
            
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-500">
                <img
                  src="/images/finance-8836902_1280.jpg"
                  alt="Investment Management Strategy"
                  className="w-full h-96 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900/50 to-transparent"></div>
              </div>
            </div>
          </div>

          {/* Services Section */}
          <div className="mb-20">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => {
                const IconComponent = service.icon;
                return (
                  <div key={index} className="group bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                    <div className="inline-flex p-4 bg-gradient-to-r from-gold-500 to-gold-600 rounded-xl mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                    <h4 className="text-xl font-bold text-navy-900 mb-4">
                      {service.title}
                    </h4>
                    <p className="text-gray-600 leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-navy-900 to-navy-800 rounded-2xl p-12 text-center text-white">
            <h3 className="text-3xl md:text-4xl font-bold font-serif mb-6">
              Ready to Start Your Investment Journey?
            </h3>
            <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
              Let our expert team help you build and manage a portfolio that aligns with your financial goals. 
              Get started with professional investment management today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="https://online.amdconsults.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-8 py-4 bg-gold-600 hover:bg-gold-700 text-white font-bold rounded-full transition-colors duration-300"
              >
                Get Started
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
              <Link 
                to="/services"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-navy-900 font-bold rounded-full transition-all duration-300"
              >
                View All Services
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InvestmentManagementPage;

